// console.debug(`${new Date().toISOString()} ${performance.now().toFixed(2)}ms [${import.meta.url}]`)
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useSessionUI } from '../../utils/composables/useSessionUI'
import type { UTCDate } from '../../utils/fechador'
import * as fechador from '../../utils/fechador'
import { log } from '../../utils/logger'
import { bootstrapCurve } from './bootstrapper'
import { fetchIRS, type FetchOptions, type PilarData, type TimeSeries } from './irsData'

const { isF3 } = useSessionUI()

// BIEST lista de codes: https://app.bde.es/bie_www/bie_wwwias/xml/Arranque.html?Idioma=es
// 'https://app.bde.es/bierest/resources/srdatosapp/favoritas?idioma=es&series=D_1T9H0011' // más reciente
// 'https://app.bde.es/bierest/resources/srdatosapp/listaSeries?idioma=es&series=D_1T9VM010&rango=30M' // minimal 30M
// 'https://app.bde.es/bierest/resources/srdatosapp/listaSeries?idioma=es&series=D_1T9VM010&rango=2026'
// https://sebgroup.com/our-offering/reports-and-publications/rates-and-iban/swap-rates

export interface Discount {
    tenor: number
    rate: number
    df: number
    forward?: number
    code?: string
}

// ****************************************************************************
// IrsStore Composition API ***************************************************
// ****************************************************************************
export const useIrsStore = defineStore('irsStore', () => {
    const irsInitialized = ref<boolean | undefined>()

    const irsData = ref<PilarData[]>([])

    const pilarSelected = ref<Discount>()
    const pilarHistory = computed<TimeSeries>(
        () =>
            irsData.value.find((s) => s.tenor === pilarSelected.value?.tenor) ?? {
                fechas: [],
                valores: []
            }
    )

    const lastIRS_30yr = computed<number | null>(() => {
        const pilar = irsData.value.find((s) => s.tenor === 30)
        if (pilar?.valores && pilar.valores.length > 0) return pilar.valores[0]
        return null
    })

    const discountCurva = ref<Discount[]>([])

    const lastIrs = ref<UTCDate>(fechador.MIN_DATE_UTC)
    const selectedIrs = ref<UTCDate>(fechador.MIN_DATE_UTC)
    const irsOptions = computed(() => {
        return (
            irsData.value[0]?.fechas.map((f) => ({
                label: fechador.toYMD(f),
                value: f
            })) ?? []
        )
    })

    /*
    El watcher corre en modo flush: 'pre' (el default), lo que significa:
    corre antes de que Vue aplique otros cambios reactivos del mismo tick
    Mode flush: 'sync' -> el watcher corre exactamente en el momento del cambio,
    sin esperar a la cola de micro‑tasks.
    */
    watch(
        selectedIrs,
        () => {
            if (!irsInitialized.value) return
            initDiscountCurva()
            // log.info('initDiscountCurva, selectedIrs:', fechador.toDateString(selectedIrs.value))

            pilarSelected.value = discountCurva.value[10]
        },
        { flush: 'sync' }
    )

    // handling error if fetch fails --> isInitialized === false
    // Called by the router if the route has: (meta: { requiresMarket: true })
    async function initIrsStore(options: FetchOptions) {
        if (irsInitialized.value !== undefined) return

        irsInitialized.value = false
        if (isF3.value) return // noMarket

        irsData.value = await fetchIRS(options)
        if (!irsData.value?.[0]?.fechas?.[0]) {
            log.warn('Falla descargar IRS de Banco de España.')
            return
        }

        // pilarSelected.value = discountCurva.value[9] // default: 20yr
        lastIrs.value = irsData.value[0].fechas[0]

        irsInitialized.value = true
    }

    function initDiscountCurva() {
        discountCurva.value = []
        const irsIndex = irsData.value[0].fechas.findIndex((f) =>
            fechador.eqDay(f, selectedIrs.value)
        )

        let prevT = 0
        let prevDF = 1
        const boostrapCurva = bootstrapCurve(irsData.value, irsIndex) // all years 0-30

        discountCurva.value = [{ tenor: 0, rate: 0, df: 1, forward: 0, code: '' }]
        for (const T of [1, 2, 3, 4, 5, 7, 10, 15, 20, 30]) {
            const point = irsData.value.find((p) => p.tenor === T)!
            const df = boostrapCurva.get(T)!
            const forward = Math.pow(prevDF / df, 1 / (T - prevT)) - 1
            const discount = {
                tenor: T,
                rate: point.valores[irsIndex] / 100,
                df: df,
                forward: forward,
                code: point.serie
            }
            discountCurva.value.push(discount)
            prevDF = df
            prevT = T
        }
        // log.debug('initDiscountCurva:', fechador.toYMD(selectedIrs.value))
    }

    function logLinearDF(month: number): number {
        // month = month + 1
        if (discountCurva.value.length === 0) return 0
        if (month < 0 || month > 372) {
            throw new Error(`month ${month} must be between 0 and 372`)
        }

        const dc = discountCurva.value
        const maxTenor = dc[dc.length - 1].tenor // normalmente 30 años
        const t = month / 12

        // 1. Si está dentro del rango → interpolación normal
        if (t <= maxTenor) {
            let lower = dc[0]
            for (const p of dc) {
                if (p.tenor <= t) lower = p
                else break
            }

            let upper = dc[dc.length - 1]
            for (const p of dc) {
                if (p.tenor >= t) {
                    upper = p
                    break
                }
            }

            if (lower.tenor === upper.tenor) return lower.df

            const w = (t - lower.tenor) / (upper.tenor - lower.tenor)
            return Math.exp(Math.log(lower.df) + w * (Math.log(upper.df) - Math.log(lower.df)))
        }

        // 2. EXTRAPOLACIÓN log-lineal para t > maxTenor
        const last = dc[dc.length - 1]
        const prev = dc[dc.length - 2]

        // pendiente logarítmica del último tramo real
        const slope = (Math.log(last.df) - Math.log(prev.df)) / (last.tenor - prev.tenor)

        // extrapolación lineal en el espacio logarítmico
        const logDFt = Math.log(last.df) + slope * (t - last.tenor)

        return Math.exp(logDFt)
    }

    return {
        initIrsStore, // async
        irsInitialized,

        pilarSelected,
        pilarHistory,

        lastIRS_30yr,

        irsOptions,
        selectedIrs,
        lastIrs,

        discountCurva,
        logLinearDF
    }
})
